import { OnChanges, SimpleChanges } from '@angular/core';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  Output,
  inject
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';

import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatCheckboxModule } from '@angular/material/checkbox';

import {
  FormFieldDefinition,
  FormFieldsBlock
} from '@core/services/ai/ai-assistant.service';

/**
 * Inline form rendered inside an assistant message. On submit, the host
 * formats the typed values into a follow-up user message ("name=value, ...")
 * and re-sends it through the chat callable so the model can stage the
 * mutate with a complete arg set.
 */
@Component({
  selector: 'app-ai-form-fields-block',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    FormsModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatCheckboxModule
  ],
  template: `
    <form class="form" (submit)="$event.preventDefault(); submit()">
      @if (block.title) {
        <header class="form__title">{{ block.title }}</header>
      }
      @for (f of block.fields; track f.name) {
        <div class="form__field">
          @switch (f.kind) {
            @case ('text') {
              <mat-form-field appearance="outline" class="form__mat-field">
                <mat-label>{{ f.label }}</mat-label>
                <input
                  matInput
                  type="text"
                  [name]="f.name"
                  [placeholder]="f.placeholder || ''"
                  [required]="!!f.required"
                  [(ngModel)]="values[f.name]" />
                @if (f.helperText) {
                  <mat-hint>{{ f.helperText }}</mat-hint>
                }
              </mat-form-field>
            }
            @case ('number') {
              <mat-form-field appearance="outline" class="form__mat-field">
                <mat-label>{{ f.label }}</mat-label>
                <input
                  matInput
                  type="number"
                  [name]="f.name"
                  [placeholder]="f.placeholder || ''"
                  [required]="!!f.required"
                  [(ngModel)]="values[f.name]" />
                @if (f.helperText) {
                  <mat-hint>{{ f.helperText }}</mat-hint>
                }
              </mat-form-field>
            }
            @case ('date') {
              <mat-form-field appearance="outline" class="form__mat-field">
                <mat-label>{{ f.label }}</mat-label>
                <input
                  matInput
                  type="date"
                  [name]="f.name"
                  [required]="!!f.required"
                  [(ngModel)]="values[f.name]" />
                @if (f.helperText) {
                  <mat-hint>{{ f.helperText }}</mat-hint>
                }
              </mat-form-field>
            }
            @case ('boolean') {
              <mat-checkbox [(ngModel)]="values[f.name]" [name]="f.name">
                {{ f.label }}
              </mat-checkbox>
            }
            @case ('select') {
              <mat-form-field appearance="outline" class="form__mat-field">
                <mat-label>{{ f.label }}</mat-label>
                <mat-select
                  [name]="f.name"
                  [required]="!!f.required"
                  [(ngModel)]="values[f.name]">
                  @for (opt of f.options; track opt.value) {
                    <mat-option [value]="opt.value">{{ opt.label }}</mat-option>
                  }
                </mat-select>
                @if (f.helperText) {
                  <mat-hint>{{ f.helperText }}</mat-hint>
                }
              </mat-form-field>
            }
          }
        </div>
      }
      <div class="form__actions">
        <button type="submit" mat-flat-button color="primary">
          {{ block.submitLabel || label('aiAssistant.widgets.submit', 'Submit') }}
        </button>
      </div>
    </form>
  `,
  styles: [
    `
      :host {
        display: block;
      }
      .form {
        background: #fff;
        border: 1px solid rgba(15, 23, 42, 0.08);
        border-radius: 12px;
        padding: 12px 14px;
        display: flex;
        flex-direction: column;
        gap: 8px;
      }
      .form__title {
        font-weight: 600;
        font-size: 14px;
        color: rgba(15, 23, 42, 0.92);
        margin-bottom: 4px;
      }
      .form__mat-field {
        width: 100%;
      }
      .form__actions {
        display: flex;
        justify-content: flex-end;
        margin-top: 4px;
      }
    `
  ]
})
export class AiFormFieldsBlockComponent implements OnChanges {
  @Input({ required: true }) block!: FormFieldsBlock;
  @Output() readonly formSubmitted = new EventEmitter<{
    formId: string;
    toolName?: string;
    values: Record<string, unknown>;
  }>();

  values: Record<string, unknown> = {};

  private readonly translate = inject(TranslateService, { optional: true });

  /** Translated label; falls back to English when no translation service/key is available. */
  protected label(key: string, fallback: string): string {
    const v = this.translate?.instant(key);
    return v && v !== key ? v : fallback;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['block']) this.seedDefaults();
  }

  private seedDefaults(): void {
    const seeded: Record<string, unknown> = {};
    for (const f of this.block.fields) {
      if ('defaultValue' in f && f.defaultValue !== undefined) {
        seeded[f.name] = f.defaultValue;
      } else if (f.kind === 'boolean') {
        seeded[f.name] = false;
      } else {
        seeded[f.name] = '';
      }
    }
    this.values = seeded;
  }

  submit(): void {
    this.formSubmitted.emit({
      formId: this.block.formId,
      toolName: this.block.toolName,
      values: { ...this.values }
    });
  }

  fieldKind(f: FormFieldDefinition): string {
    return f.kind;
  }
}
